import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Button, Grid, Card, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  LinearProgress, TextField
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  EmojiEmotions as EmojiIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const EmojiPicker = ({ open, onClose, onInsert, editor }) => {
  const [emojiLibrary, setEmojiLibrary] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inserting, setInserting] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    file: null
  });
  const fileInputRef = useRef(null);

  // Load emojis when dialog opens
  useEffect(() => {
    if (open) {
      loadEmojis();
    }
  }, [open]);

  const loadEmojis = async () => {
    setLoading(true);
    try {
      const api = (await import('../api')).default;
      const response = await api.get('/emojis?type=USER_UPLOADED&limit=50');
      
      if (response.data.success) {
        // Emojis should now be using GCP URLs directly, no conversion needed
        setEmojiLibrary(response.data.data.emojis);
      }
    } catch (error) {
      console.error('Error loading emojis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadForm(prev => ({
        ...prev,
        file,
        name: prev.name || file.name.replace(/\.[^/.]+$/, "")
      }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.name.trim()) {
      alert('Please select a file and provide a name');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('emoji', uploadForm.file);
      formData.append('name', uploadForm.name.trim());
      formData.append('description', uploadForm.description.trim());
      formData.append('category', 'CUSTOM');
      formData.append('tags', 'custom,user');
      formData.append('isPublic', 'false');

      const api = (await import('../api')).default;
      const response = await api.post('/emojis/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.success) {
        // Clear form
        setUploadForm({ name: '', description: '', file: null });
        fileInputRef.current.value = '';
        
        // Reload emojis - but don't call it immediately to avoid loops
        setTimeout(loadEmojis, 100);
      } else {
        throw new Error('Failed to upload emoji');
      }
    } catch (error) {
      console.error('Error uploading emoji:', error);
      alert('Failed to upload emoji. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const insertEmoji = (emoji) => {
    if (!editor || inserting) return;
    
    setInserting(true);
    
    try {
      // Use the direct GCP URL for emojis
      const imageUrl = emoji.imageUrl;
      
      console.log('🎭 Inserting emoji with simple HTML approach:', {
        name: emoji.name,
        src: imageUrl,
        alt: `emoji-${emoji.name}`,
        title: emoji.name
      });
      
      // Simple emoji that scales with text size only
      const emojiHtml = `<img class="emoji-inline" data-emoji-name="${emoji.name}" src="${imageUrl}" alt="emoji-${emoji.name}" title="${emoji.name}" style="height: 1em; width: auto; display: inline; vertical-align: middle; margin: 0 0.1em; object-fit: contain; border: none; background: transparent; font-size: inherit; line-height: 1;">`;
      
      editor.chain().focus().insertContent(emojiHtml).run();
      
      handleClose();
    } catch (error) {
      console.error('Error inserting emoji:', error);
    } finally {
      setTimeout(() => setInserting(false), 100);
    }
  };

  const handleClose = () => {
    setUploadForm({ name: '', description: '', file: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setLoading(false);
    setInserting(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EmojiIcon color="primary" />
        Custom Emoji Manager
      </DialogTitle>
      
      <DialogContent>
        {/* Upload Section */}
        <Box sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Upload New Emoji
          </Typography>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                startIcon={<UploadIcon />}
                fullWidth
                disabled={uploading}
              >
                {uploadForm.file ? `Selected: ${uploadForm.file.name}` : 'Choose Image File'}
              </Button>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Emoji Name"
                value={uploadForm.name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
                size="small"
                required
                helperText="Give your emoji a descriptive name"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description (optional)"
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                fullWidth
                size="small"
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={uploading || !uploadForm.file || !uploadForm.name.trim()}
                startIcon={uploading ? <LinearProgress /> : <UploadIcon />}
                fullWidth
              >
                {uploading ? 'Uploading...' : 'Upload Emoji'}
              </Button>
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Tip: Upload small, clear images. They will be optimized automatically for emoji use.
          </Typography>
        </Box>

        {/* Emoji Library */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Your Custom Emojis ({emojiLibrary.length})
          </Typography>
          
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>Loading emojis...</Typography>
            </Box>
          ) : emojiLibrary.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No custom emojis yet. Upload your first emoji above!
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={1}>
              {emojiLibrary.map((emoji) => (
                <Grid item xs={3} sm={2} key={emoji.id}>
                  <Tooltip title={`${emoji.name}${emoji.description ? ` - ${emoji.description}` : ''}`}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        textAlign: 'center',
                        p: 1,
                        aspectRatio: '1',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': { 
                          bgcolor: 'primary.light',
                          transform: 'scale(1.05)',
                          transition: 'all 0.2s'
                        }
                      }}
                      onClick={() => !inserting && insertEmoji(emoji)}
                    >
                      <img
                        src={emoji.imageUrl}
                        alt={emoji.name}
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'contain',
                          marginBottom: '4px'
                        }}
                        onError={(e) => {
                          console.warn('Failed to load emoji image:', emoji.imageUrl);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <Box 
                        sx={{ 
                          display: 'none', 
                          width: '40px', 
                          height: '40px', 
                          bgcolor: 'grey.200', 
                          borderRadius: '4px',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '4px'
                        }}
                      >
                        <Typography variant="caption">❓</Typography>
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {emoji.name.length > 8 ? emoji.name.substring(0, 8) + '...' : emoji.name}
                      </Typography>
                    </Card>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmojiPicker;